import { useEffect, useId, useRef, useState } from 'react'
import { TotpQrCode } from './totp-qr-code'
import { formatSetupKey, isCompleteTotpCode } from '../totp-code'

type Props = {
  secret: string
  otpauthUri: string
  submitting: boolean
  error: string | null
  submitLabel: string
  onSubmit: (code: string) => void
  onCancel?: () => void
  cancelLabel?: string
}

const AUTHENTICATOR_APPS = 'Google Authenticator, Microsoft Authenticator, 1Password or Authy'

/**
 * Presentational half of authenticator enrolment: QR, manual setup key, and
 * the confirming code. Deliberately owns no Cognito calls — the settings
 * screen verifies with `verifyTOTPSetup`, while the sign-in screen answers a
 * challenge with `confirmSignIn`, and both need this exact surface.
 */
export function TotpEnrolmentPanel({
  secret,
  otpauthUri,
  submitting,
  error,
  submitLabel,
  onSubmit,
  onCancel,
  cancelLabel = 'Cancel',
}: Props) {
  const [code, setCode] = useState('')
  const [copied, setCopied] = useState(false)
  const codeFieldId = useId()
  const setupKeyId = useId()
  const codeInputRef = useRef<HTMLInputElement>(null)

  // A fresh secret means a fresh attempt: never carry a stale code across.
  useEffect(() => {
    setCode('')
    setCopied(false)
  }, [secret])

  useEffect(() => {
    if (error) codeInputRef.current?.focus()
  }, [error])

  const copySetupKey = async () => {
    try {
      await navigator.clipboard.writeText(secret)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2500)
    } catch {
      // Clipboard is blocked in some embedded webviews; the key is on screen.
      setCopied(false)
    }
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (!submitting && isCompleteTotpCode(code)) onSubmit(code)
      }}
    >
      <ol className="space-y-1 text-sm text-slate-600">
        <li>1. Install an authenticator app ({AUTHENTICATOR_APPS}).</li>
        <li>2. Scan this QR code, or enter the setup key by hand.</li>
        <li>3. Type the six-digit code it shows to confirm.</li>
      </ol>

      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <TotpQrCode otpauthUri={otpauthUri} />
        <div className="min-w-0 flex-1">
          <p id={setupKeyId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Setup key
          </p>
          <p className="mt-1 break-all font-mono text-sm text-slate-900">{formatSetupKey(secret)}</p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void copySetupKey()}
              aria-describedby={setupKeyId}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-100"
            >
              {copied ? 'Copied' : 'Copy setup key'}
            </button>
            <a
              href={otpauthUri}
              className="text-xs font-semibold text-brand-700 underline-offset-2 hover:underline"
            >
              Open in authenticator app
            </a>
          </div>
          <p className="sr-only" role="status" aria-live="polite">
            {copied ? 'Setup key copied to the clipboard' : ''}
          </p>
        </div>
      </div>

      {error && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700"
          role="alert"
        >
          {error}
        </div>
      )}

      <label htmlFor={codeFieldId} className="block text-sm font-medium text-slate-700">
        Six-digit code
      </label>
      <input
        id={codeFieldId}
        ref={codeInputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={7}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        aria-invalid={Boolean(error)}
        className="w-40 rounded-xl border border-slate-200 px-3 py-2.5 font-mono text-lg tracking-widest outline-none ring-brand-500 focus:ring-2"
      />

      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={submitting || !isCompleteTotpCode(code)}
          className="rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Checking code…' : submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
        )}
      </div>
    </form>
  )
}
